/**
 * Slug generation and validation utilities for Putty's URL grammar.
 *
 * Four slug-shaped identifiers exist:
 *
 * 1. **Block slug** — `putty.me/b/{slug}`. Pure random 8-char base62 nanoid.
 *    Generated at block creation, immutable. 62^8 ≈ 2.18 × 10^14 keyspace.
 *    Same approach as YouTube video IDs: opaque, stable, never derived from
 *    content.
 *
 * 2. **Root-namespace slug** — `putty.me/{name}` (username OR layer-0 group).
 *    Lowercase, hyphen-separated, 3–20 chars. Shared global namespace.
 *
 * 3. **Child collection slug** — `putty.me/{parent}/{slug}`. Same character
 *    rules as root; uniqueness scope is per-parent.
 *
 * 4. **Standalone collection slug** — `putty.me/c/{slug}`. Title-derived
 *    prefix + `-` + 8-char base62 suffix, e.g. `stephen-kevin-aB12cD3e`.
 *    Globally unique among standalone (parent-less, layer-1) collections.
 *    The suffix's keyspace lets every collection get a slug at creation
 *    time with no collision-retry loop; the title prefix keeps the URL
 *    readable.
 *
 * Reserved root names live in `./reserved-names.ts` and apply to root-namespace
 * slugs only.
 */
import { customAlphabet } from "nanoid";
// ---------------------------------------------------------------------------
// Block slugs (random base62, immutable)
// ---------------------------------------------------------------------------
/** Length of the random portion of a block slug. */
export const BLOCK_SLUG_LENGTH = 8;
/**
 * Base62 alphabet used for block slugs. Mixed-case for keyspace; the URL is
 * case-sensitive (per RFC 3986 path segments), so `aB1` and `Ab1` are distinct.
 */
const BLOCK_SLUG_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const generateBlockSlugInternal = customAlphabet(BLOCK_SLUG_ALPHABET, BLOCK_SLUG_LENGTH);
/**
 * Generate a fresh random block slug.
 *
 * Collision handling is the caller's responsibility — the DB unique index is
 * the source of truth. On conflict, the entity layer should retry generation.
 *
 * @example
 * generateBlockSlug() // "aB12cD3e"
 */
export function generateBlockSlug() {
    return generateBlockSlugInternal();
}
/**
 * Pattern matching valid block slugs (8 base62 chars).
 */
export const BLOCK_SLUG_PATTERN = new RegExp(`^[0-9A-Za-z]{${BLOCK_SLUG_LENGTH}}$`);
/**
 * Returns true if a candidate string is a syntactically valid block slug.
 */
export function isValidBlockSlug(value) {
    return BLOCK_SLUG_PATTERN.test(value);
}
// ---------------------------------------------------------------------------
// Name slugs (root namespace + child collection slugs)
// ---------------------------------------------------------------------------
/** Min length for usernames, group slugs, and collection slugs. */
export const MIN_NAME_SLUG_LENGTH = 3;
/** Max length for usernames, group slugs, and collection slugs. */
export const MAX_NAME_SLUG_LENGTH = 20;
/**
 * Pattern for valid name slugs:
 *   - lowercase letters, digits, hyphens
 *   - cannot start or end with a hyphen
 *   - no consecutive hyphens
 *   - length 3–20
 *
 * Examples (valid):  "alex", "loop-fans", "best-memes-2024"
 * Examples (invalid): "-alex", "alex-", "loop--fans", "Alex", "ab"
 */
export const NAME_SLUG_PATTERN = new RegExp(`^[a-z0-9](?:[a-z0-9]|-(?!-)){${MIN_NAME_SLUG_LENGTH - 2},${MAX_NAME_SLUG_LENGTH - 2}}[a-z0-9]$`);
/**
 * Returns true if `value` is a syntactically valid name slug. Does NOT check
 * the reserved-names list — call `isReservedRootName` separately for root-level
 * claims.
 */
export function isValidNameSlug(value) {
    return NAME_SLUG_PATTERN.test(value);
}
/**
 * Convert an arbitrary title to a valid name slug.
 *
 * Steps:
 *   1. Lowercase
 *   2. Strip diacritics (NFKD normalize + drop combining marks)
 *   3. Replace any run of non-alphanumeric chars with a single hyphen
 *   4. Trim leading/trailing hyphens
 *   5. Truncate to MAX_NAME_SLUG_LENGTH (cleaning a trailing hyphen if produced)
 *
 * If the result is shorter than MIN_NAME_SLUG_LENGTH, returns `undefined` —
 * caller should fall back to a synthetic slug (e.g. an entity ID-derived stub).
 *
 * @example
 * slugifyTitle("Best Memes 2024")  // "best-memes-2024"
 * slugifyTitle("  ¡Hola, Mundo!  ") // "hola-mundo"
 * slugifyTitle("a")                 // undefined (too short)
 */
export function slugifyTitle(title) {
    const normalized = title
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    if (normalized.length < MIN_NAME_SLUG_LENGTH) {
        return undefined;
    }
    let truncated = normalized.slice(0, MAX_NAME_SLUG_LENGTH);
    while (truncated.endsWith("-")) {
        truncated = truncated.slice(0, -1);
    }
    if (truncated.length < MIN_NAME_SLUG_LENGTH) {
        return undefined;
    }
    return truncated;
}
/**
 * Append a `-N` suffix to a base slug, where N is the smallest integer ≥ 2 not
 * present in `existing`. Used by entity layers to deduplicate within a parent
 * scope.
 *
 * If appending the suffix would exceed MAX_NAME_SLUG_LENGTH, the base portion
 * is truncated (and any trailing hyphen cleaned) before the suffix is added.
 *
 * @example
 * dedupeNameSlug("best-memes", new Set(["best-memes"]))                 // "best-memes-2"
 * dedupeNameSlug("best-memes", new Set(["best-memes", "best-memes-2"])) // "best-memes-3"
 * dedupeNameSlug("best-memes", new Set())                                // "best-memes" (already unique)
 */
export function dedupeNameSlug(base, existing) {
    if (!existing.has(base)) {
        return base;
    }
    for (let n = 2; n < 10000; n++) {
        const suffix = `-${n}`;
        const maxBaseLength = MAX_NAME_SLUG_LENGTH - suffix.length;
        let truncatedBase = base.slice(0, maxBaseLength);
        while (truncatedBase.endsWith("-")) {
            truncatedBase = truncatedBase.slice(0, -1);
        }
        const candidate = `${truncatedBase}${suffix}`;
        if (!existing.has(candidate)) {
            return candidate;
        }
    }
    // 9998 collisions on a single base is implausible; treat as a programming
    // error so the caller surfaces it rather than looping forever.
    throw new Error(`dedupeNameSlug: exhausted suffix attempts for base "${base}"`);
}
/**
 * Normalize a candidate name to lowercase for comparison/storage. Whitespace
 * is trimmed; no other transforms are applied (use `slugifyTitle` for that).
 */
export function normalizeNameSlug(value) {
    return value.trim().toLowerCase();
}
// ---------------------------------------------------------------------------
// Standalone collection slugs (title-derived prefix + random suffix)
// ---------------------------------------------------------------------------
/** Length of the random suffix appended to a standalone-collection slug. */
export const STANDALONE_SLUG_SUFFIX_LENGTH = 8;
/**
 * Fallback prefix when a collection has no title, or the title is too short
 * to produce a usable name slug. Picked to read sensibly in a URL —
 * `/c/untitled-aB12cD3e` is parsable.
 */
const STANDALONE_SLUG_FALLBACK_PREFIX = "untitled";
/**
 * Maximum total length of a standalone-collection slug:
 *
 *   prefix (≤ MAX_NAME_SLUG_LENGTH) + "-" + suffix (STANDALONE_SLUG_SUFFIX_LENGTH)
 *
 * The DB column is `VARCHAR(60)`, so this leaves plenty of headroom.
 */
export const MAX_STANDALONE_SLUG_LENGTH = MAX_NAME_SLUG_LENGTH + 1 + STANDALONE_SLUG_SUFFIX_LENGTH;
const generateStandaloneSuffixInternal = customAlphabet(
// Same base62 alphabet block slugs use — case-sensitive, URL-safe.
"0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz", STANDALONE_SLUG_SUFFIX_LENGTH);
/**
 * Generate the random 8-char suffix portion of a standalone collection slug.
 * Exposed separately so backfill scripts and tests can build slugs with a
 * known prefix.
 */
export function generateStandaloneSlugSuffix() {
    return generateStandaloneSuffixInternal();
}
/**
 * Build a fresh, globally-unique slug for a standalone (parent-less,
 * layer-1) collection. The result is always shaped `{prefix}-{suffix}`:
 *
 *   - `prefix` — `slugifyTitle(title)` when one survives the transform,
 *     otherwise `"untitled"`. Always ≤ MAX_NAME_SLUG_LENGTH chars.
 *   - `suffix` — fresh 8-char base62, no relationship to the title.
 *
 * Collision retry is the caller's responsibility, but with 62⁸ ≈ 2.18 × 10¹⁴
 * possible suffixes per prefix, retries should be vanishingly rare and the
 * DB partial unique index is the source of truth.
 *
 * @example
 * generateStandaloneCollectionSlug("Stephen & Kevin") // "stephen-kevin-aB12cD3e"
 * generateStandaloneCollectionSlug()                  // "untitled-aB12cD3e"
 * generateStandaloneCollectionSlug("???")             // "untitled-aB12cD3e"
 */
export function generateStandaloneCollectionSlug(title) {
    const titleSlug = title !== undefined ? slugifyTitle(title) : undefined;
    const prefix = titleSlug ?? STANDALONE_SLUG_FALLBACK_PREFIX;
    return `${prefix}-${generateStandaloneSlugSuffix()}`;
}
/**
 * Pattern matching valid standalone-collection slugs:
 *
 *   {name-slug-style prefix, 3–20 chars}-{8 base62 chars}
 *
 * The body before the final `-` follows the same rules as `NAME_SLUG_PATTERN`
 * (lowercase letters/digits with non-leading/trailing/consecutive hyphens),
 * and the trailing 8 chars are case-sensitive base62 — matching exactly what
 * `generateStandaloneCollectionSlug` produces.
 *
 * Used by the public resolver to reject malformed URLs cheaply before any
 * DB lookup.
 */
export const STANDALONE_SLUG_PATTERN = new RegExp(`^[a-z0-9](?:[a-z0-9]|-(?!-)){${MIN_NAME_SLUG_LENGTH - 2},${MAX_NAME_SLUG_LENGTH - 2}}[a-z0-9]-[0-9A-Za-z]{${STANDALONE_SLUG_SUFFIX_LENGTH}}$`);
/**
 * Returns true if `value` looks like a slug produced by
 * `generateStandaloneCollectionSlug`. The check is permissive about the
 * exact prefix content (any valid name-slug body is fine) so titles that
 * round-trip through `slugifyTitle` always validate.
 */
export function isValidStandaloneCollectionSlug(value) {
    return STANDALONE_SLUG_PATTERN.test(value);
}
